// @ts-nocheck
import React, { useState, useEffect } from "react";
import SharedSelect from "../components/SharedSelect";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { getUser } from "../auth/auth";
import TopNav from "../components/TopNav";
import LocationPickerModal from "../components/LocationPickerModal";
import { useToast } from "../components/ToastProvider";
import "../styles/PostBounty.css";
import { createBounty, getBounties } from "../api/bounties";
import { getMonetizationMe } from "../api/monetization";
import { buildCardDetailUrl } from "../utils/marketAnalytics";
import { getCardMarket } from "../api/cards";
import CardAutocompleteInput from "../components/CardAutocompleteInput";
import "../styles/CardAutocomplete.css";
import {
  deleteDraftImage,
  getDraftImage,
  saveDraftImage,
} from "../utils/draftImageStore";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFile,
} from "../utils/imageUploadValidation";
import { formatLocationLabel, getProfileLocation } from "../utils/location";

const BOUNTY_DRAFT_VERSION = 1;
const BOUNTY_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const BOUNTY_DEFAULT_FORM = {
  cardName: "",
  game: "Magic: The Gathering",
  gradingCompany: "raw",
  gradeNumber: "",
  desiredCondition: "Near Mint",
  zipCode: "",
  radius: 25,
};

function PostBounty() {
  const navigate = useNavigate();
  const user = getUser();
  const profileLocation = getProfileLocation(user);
  const { showToast } = useToast();
  const userId = user?.id ?? null;
  const draftScope = userId || "guest";
  const draftStorageKey = `postBountyDraft:${draftScope}`;
  const draftImageKey = `postBountyDraftImage:${draftScope}`;

  const [formData, setFormData] = useState(BOUNTY_DEFAULT_FORM);

  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [userActiveBounties, setUserActiveBounties] = useState(0);
  const [maxActiveBounties, setMaxActiveBounties] = useState(2);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [marketHint, setMarketHint] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(profileLocation);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const isOnePieceGame = formData.game.trim().toLowerCase() === "one piece";

  const resolveCompressionFn = () => {
    if (typeof imageCompression === "function") return imageCompression;
    if (imageCompression && typeof imageCompression.default === "function") {
      return imageCompression.default;
    }
    return null;
  };

  useEffect(() => {
    if (userId) {
      Promise.all([getBounties({ userId, status: "active" }), getMonetizationMe()])
        .then(([data, monetization]) => {
          setUserActiveBounties((data?.items || []).length);
          setMaxActiveBounties(Number(monetization?.plan?.maxActiveBounties || 2));
        })
        .catch(err => console.error("Error fetching bounties:", err));
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;

    const restoreDraft = async () => {
      const serializedDraft = sessionStorage.getItem(draftStorageKey);
      if (serializedDraft) {
        try {
          const parsed = JSON.parse(serializedDraft);
          const isExpired =
            !parsed?.updatedAt || Date.now() - parsed.updatedAt > BOUNTY_DRAFT_TTL_MS;

          if (parsed?.version === BOUNTY_DRAFT_VERSION && !isExpired && parsed?.formData) {
            setFormData(prev => ({ ...prev, ...parsed.formData }));
            if (parsed?.formData?.zipCode) {
              setSelectedLocation({
                postalCode: parsed.formData.zipCode,
                countryCode: "US",
                label: parsed.formData.zipCode,
                latitude: null,
                longitude: null,
              });
            }
          } else {
            sessionStorage.removeItem(draftStorageKey);
            await deleteDraftImage(draftImageKey).catch(() => undefined);
          }
        } catch (error) {
          sessionStorage.removeItem(draftStorageKey);
        }
      }

      try {
        const imageRecord = await getDraftImage(draftImageKey);
        if (!cancelled && imageRecord) {
          const isExpired = Date.now() - imageRecord.updatedAt > BOUNTY_DRAFT_TTL_MS;
          if (isExpired) {
            await deleteDraftImage(draftImageKey).catch(() => undefined);
          } else {
            const restoredFile = new File([imageRecord.blob], imageRecord.meta.name || "bounty-image.webp", {
              type: imageRecord.meta.type || "image/webp",
              lastModified: imageRecord.meta.lastModified || Date.now(),
            });
            setPhotoFile(restoredFile);
            const restoredPreview = URL.createObjectURL(restoredFile);
            setPhotoPreviewUrl(prev => {
              if (prev) URL.revokeObjectURL(prev);
              return restoredPreview;
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setPhotoError("Draft text restored. Please reselect your image.");
        }
      } finally {
        if (!cancelled) {
          setDraftHydrated(true);
        }
      }
    };

    void restoreDraft();

    return () => {
      cancelled = true;
    };
  }, [draftStorageKey, draftImageKey]);

  useEffect(() => {
    if (!draftHydrated && profileLocation?.postalCode) {
      setFormData((prev) => ({ ...prev, zipCode: prev.zipCode || profileLocation.postalCode }));
      setSelectedLocation(profileLocation);
    }
  }, [draftHydrated, profileLocation]);

  useEffect(() => {
    if (!draftHydrated) return;
    const timeoutId = setTimeout(() => {
      try {
        sessionStorage.setItem(
          draftStorageKey,
          JSON.stringify({
            version: BOUNTY_DRAFT_VERSION,
            updatedAt: Date.now(),
            formData,
          })
        );
      } catch (error) {
        console.warn("Failed to persist bounty draft.", error);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [formData, draftHydrated, draftStorageKey]);

  useEffect(() => {
    const scroller = document.querySelector(".app-scroll-root");
    if (scroller) {
      scroller.scrollTo({ top: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
    };
  }, [photoPreviewUrl]);

  const clearDraftState = async () => {
    sessionStorage.removeItem(draftStorageKey);
    await deleteDraftImage(draftImageKey).catch(() => undefined);
  };

  const handleCancel = async () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setFormData(BOUNTY_DEFAULT_FORM);
    setPhotoFile(null);
    setPhotoPreviewUrl("");
    setPhotoError("");
    setSelectedCard(null);
    setMarketHint("");
    await clearDraftState();
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  useEffect(() => {
    setSelectedCard(null);
  }, [formData.game]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const applyImage = async () => {
      const validation = validateImageFile(file, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
      if (!validation.ok) {
        setPhotoError(validation.error || "Please upload a valid image.");
        e.target.value = "";
        return;
      }

      try {
        setPhotoError("");
        const compress = resolveCompressionFn();
        const compressedFile = compress
          ? await compress(file, {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1600,
              useWebWorker: true,
            })
          : file;

        if (compressedFile.size > MAX_IMAGE_BYTES_DEFAULT) {
          setPhotoError("Image is too large after compression. Please pick a smaller image.");
          e.target.value = "";
          return;
        }

        const normalizedFile = compressedFile instanceof File
          ? compressedFile
          : new File([compressedFile], file.name || "bounty-image.webp", { type: compressedFile.type || file.type || "image/webp" });
        setPhotoFile(normalizedFile);
        setPhotoPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(normalizedFile);
        });
        await saveDraftImage(draftImageKey, normalizedFile, {
          name: normalizedFile.name || "bounty-image.webp",
          type: normalizedFile.type || "image/webp",
          lastModified: normalizedFile.lastModified || Date.now(),
        });
      } catch (error) {
        setPhotoError("Could not process that image. Try another one.");
      }
    };

    void applyImage();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      showToast("Please log in to post a bounty", "warning");
      navigate("/login");
      return;
    }

    if (userActiveBounties >= maxActiveBounties) {
      showToast(`Plan limit reached: your plan allows ${maxActiveBounties} active bounties.`, "warning");
      return;
    }

    if (!formData.cardName) {
      showToast("Please fill in all required fields", "warning");
      return;
    }
    if (!selectedLocation && !formData.zipCode) {
      showToast("Set where this bounty should be active before posting.", "warning");
      return;
    }
    if (!isOnePieceGame && !selectedCard) {
      showToast("Please select a valid card from suggestions", "warning");
      return;
    }

    if (!photoFile) {
      showToast("Please upload a bounty image.", "warning");
      return;
    }

    setLoading(true);

    try {
      await createBounty({
        userId: user.id,
        cardName: formData.cardName,
        game: formData.game,
        cardGame: selectedCard?.game,
        cardId: selectedCard?.cardId,
        cardNameCanonical: selectedCard?.cardName,
        desiredCondition: formData.desiredCondition,
        gradingCompany: formData.gradingCompany,
        gradeNumber: formData.gradeNumber,
        zipCode: formData.zipCode || undefined,
        radius: parseInt(formData.radius),
        file: photoFile,
      });

      await clearDraftState();
      showToast("Bounty posted successfully! We'll notify you when someone nearby has this card.", "success");
      navigate("/local-trades");
    } catch (error) {
      console.error("Error posting bounty:", error);
      showToast(error?.message || "Failed to post bounty. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadMarket = async () => {
      if (isOnePieceGame) {
        setMarketHint("Price unavailable");
        return;
      }
      if (!selectedCard?.cardId || !selectedCard?.game) {
        setMarketHint("");
        return;
      }
      try {
        const data = await getCardMarket({ game: selectedCard.game, cardId: selectedCard.cardId });
        if (!active) return;
        const low = data?.market?.low;
        const high = data?.market?.high;
        if (low != null && high != null) {
          setMarketHint(`Market range: $${low}-$${high}`);
        } else {
          setMarketHint("Price unavailable");
        }
      } catch {
        if (active) setMarketHint("Price unavailable");
      }
    };
    void loadMarket();
    return () => {
      active = false;
    };
  }, [selectedCard, isOnePieceGame]);

  return (
    <>
      <TopNav />
      <div className="post-bounty-page page-with-top-nav">
        <div className="bounty-container">
          <div className="bounty-header">
            <button className="back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
            <div>
              <h1>Post a Bounty</h1>
              <p className="progress-text">Post a bounty → receive offers → meet safely → rate trade</p>
            </div>
          </div>

          <div className="bounty-card">
            <form className="bounty-form" onSubmit={handleSubmit}>
              {userActiveBounties >= 3 && (
                <div className="warning-banner">
                  ⚠️ You already have 3 active bounties. Complete or delete one to post another.
                </div>
              )}

              {/* Card Details Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">CARD DETAILS</h2>
                <div className="section-divider"></div>

                <div className="form-group full-width">
                  <label>Reference Image *</label>
                  <div className="photo-dropzone">
                    {!photoPreviewUrl ? (
                      <label className="dropzone-label">
                        <input
                          type="file"
                          accept={IMAGE_INPUT_ACCEPT_ATTR}
                          onChange={handlePhotoChange}
                          style={{ display: 'none' }}
                        />
                        <div className="dropzone-content">
                          <span className="upload-icon">📷</span>
                          <span className="upload-text upload-text-glow">Upload a reference image of the card you're looking for.</span>
                        </div>
                        <p className="dropzone-helper">
                          This image is for identification only. You don't need to own the card.
                        </p>
                      </label>
                    ) : (
                      <div className="photo-preview-box">
                        <span className="reference-badge">Reference</span>
                        <img src={photoPreviewUrl} alt="Card preview" />
                        <label className="replace-photo-link">
                          <input
                          type="file"
                          accept={IMAGE_INPUT_ACCEPT_ATTR}
                          onChange={handlePhotoChange}
                          style={{ display: 'none' }}
                        />
                        Replace photo
                      </label>
                    </div>
                  )}
                  {photoError && <p className="dropzone-helper" style={{ color: "#ff8f8f" }}>{photoError}</p>}
                </div>
              </div>
                <div className="form-group full-width">
                  <label>Card Name *</label>
                  <div className="input-with-feedback">
                    <CardAutocompleteInput
                      id="bountyCardName"
                      game={formData.game}
                      value={formData.cardName}
                      onValueChange={(nextValue) =>
                        setFormData((prev) => ({ ...prev, cardName: nextValue }))
                      }
                      selectedCard={selectedCard}
                      onSelectCard={setSelectedCard}
                      placeholder="Search and select card"
                      required
                    />
                    {selectedCard && <span className="check-icon">✓</span>}
                  </div>
                  {isOnePieceGame && (
                    <p className="dropzone-helper">One Piece canonical validation coming soon.</p>
                  )}
                  {selectedCard && (
                    <button
                      type="button"
                      className="market-hint-inline"
                      onClick={() =>
                        navigate(
                          buildCardDetailUrl(
                            selectedCard.cardId,
                            selectedCard.cardName,
                            selectedCard.game
                          )
                        )
                      }
                    >
                      {marketHint || "Loading market data..."} · View card details
                    </button>
                  )}
                </div>

                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Game *</label>
                    <SharedSelect name="game" value={formData.game} onChange={handleChange}>
                      <option>Magic: The Gathering</option>
                      <option>Pokémon</option>
                      <option>Yu-Gi-Oh!</option>
                      <option>One Piece</option>
                    </SharedSelect>
                  </div>

                  <div className="form-group">
                    <label>Grading Company</label>
                    <SharedSelect name="gradingCompany" value={formData.gradingCompany} onChange={handleChange}>
                      <option value="">Any</option>
                      <option value="raw">Raw (Ungraded)</option>
                      <option value="psa">PSA</option>
                      <option value="bgs">BGS/Beckett</option>
                      <option value="cgc">CGC</option>
                      <option value="tag">TAG</option>
                      <option value="sgc">SGC</option>
                      <option value="other">Other Graded</option>
                    </SharedSelect>
                  </div>
                </div>

                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Grade Number</label>
                    <SharedSelect name="gradeNumber" value={formData.gradeNumber} onChange={handleChange} disabled={formData.gradingCompany === "raw"}>
                      <option value="">Any</option>
                      <option value="10">10</option>
                      <option value="9.5">9.5</option>
                      <option value="9">9</option>
                      <option value="8.5">8.5</option>
                      <option value="8">8</option>
                      <option value="7.5">7.5</option>
                      <option value="7">7</option>
                      <option value="6">6</option>
                      <option value="5">5</option>
                      <option value="4">4</option>
                      <option value="3">3</option>
                      <option value="2">2</option>
                      <option value="1">1</option>
                    </SharedSelect>
                  </div>

                  <div className="form-group">
                    <label>Condition *</label>
                    <SharedSelect name="desiredCondition" value={formData.desiredCondition} onChange={handleChange} disabled={formData.gradingCompany !== "raw"}>
                      <option value="">Any</option>
                      <option>Near Mint</option>
                      <option>Lightly Played</option>
                      <option>Moderately Played</option>
                      <option>Heavily Played</option>
                      <option>Damaged</option>
                    </SharedSelect>
                  </div>
                </div>
              </div>

              {/* Location Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">TRADE RADIUS</h2>
                <div className="section-divider"></div>
                
                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Bounty Location *</label>
                    <div className="location-value" style={{ marginBottom: "10px" }}>
                      {formatLocationLabel(selectedLocation, "No bounty location selected")}
                    </div>
                    <div className="location-actions" style={{ marginBottom: "12px" }}>
                      <button type="button" className="btn-cancel" onClick={() => setLocationModalOpen(true)}>
                        {selectedLocation ? "Change location" : "Set location"}
                      </button>
                      {profileLocation ? (
                        <button
                          type="button"
                          className="btn-save"
                          onClick={() => {
                            setSelectedLocation(profileLocation);
                            setFormData((prev) => ({ ...prev, zipCode: profileLocation.postalCode }));
                          }}
                        >
                          Use profile location
                        </button>
                      ) : null}
                    </div>
                    <label>Your Postal Code *</label>
                    <div className="input-with-feedback">
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 16);
                          setFormData(prev => ({ ...prev, zipCode: value }));
                          setSelectedLocation((prev) =>
                            prev && prev.postalCode === value
                              ? prev
                              : value
                                ? {
                                    postalCode: value,
                                    countryCode: prev?.countryCode || "US",
                                    label: prev?.postalCode === value ? prev.label : value,
                                    latitude: prev?.postalCode === value ? prev.latitude : null,
                                    longitude: prev?.postalCode === value ? prev.longitude : null,
                                  }
                                : null,
                          );
                        }}
                        placeholder="e.g., 10001 / SW1A 1AA"
                        maxLength="16"
                      />
                      {formData.zipCode.trim().length >= 3 && <span className="check-icon">✓</span>}
                    </div>
                    {formData.zipCode.trim().length >= 3 && <span className="success-text">Only post cards that are currently available in this area.</span>}
                  </div>

                  <div className="form-group">
                    <label>Search Radius</label>
                    <SharedSelect name="radius" value={formData.radius} onChange={handleChange}>
                      <option value={10}>10 miles</option>
                      <option value={25}>25 miles</option>
                      <option value={50}>50 miles</option>
                      <option value={100}>100 miles</option>
                    </SharedSelect>
                  </div>
                </div>
              </div>

              {/* Safety Guidelines Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">SAFETY GUIDELINES</h2>
                <div className="section-divider"></div>
                
                <ul className="safety-list">
                  <li>Always meet in public places during daylight hours</li>
                  <li>Bring a friend if trading expensive cards</li>
                  <li>Inspect cards carefully before completing trade</li>
                  <li>Never share your address until trade is confirmed</li>
                  <li>Report suspicious behavior immediately</li>
                </ul>
              </div>

              {/* How Bounties Work */}
              <div className="info-box game-tooltip">
                <h3>💡 How Your Bounty Matches</h3>
                <ul>
                  <li>🎯 Your bounty will be matched with local traders who have this card</li>
                  <li>🔔 You'll get notified when someone posts a matching trade</li>
                  <li>💬 Traders with the card can send you trade proposals</li>
                  <li>🤝 Negotiate details and arrange a safe public meetup</li>
                </ul>
              </div>

              {/* Form Actions */}
              <div className="decision-moment">
                <p className="decision-text">You're about to alert nearby traders.</p>
              </div>
              <div className="form-footer">
                <button type="button" className="btn-cancel" onClick={() => void handleCancel()}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={loading || userActiveBounties >= 3}>
                  {loading ? "Posting..." : "Post Bounty"}
                </button>
              </div>
              <p className="trust-reassurance">
                🔒 No personal information is shared on Cardex.
              </p>
            </form>
          </div>
        </div>
      </div>
      <LocationPickerModal
        isOpen={locationModalOpen}
        title="Bounty Location"
        subtitle="Use the area where you want Cardex to look for matching local inventory."
        confirmLabel="Use this bounty location"
        initialLocation={selectedLocation || profileLocation}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={async (location) => {
          setSelectedLocation(location);
          setFormData((prev) => ({ ...prev, zipCode: location.postalCode }));
        }}
      />
    </>
  );
}

export default PostBounty;
